/**
 * Shop the Look — custom tabs: hotspot dots sync side panels / product popovers.
 * Attributes: on-hover, product-card-dot (high_energy)
 */

class ShopTheLookTabs extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {boolean} */
  #productCardDot = false;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#productCardDot = this.hasAttribute('product-card-dot');
    const useHover = this.hasAttribute('on-hover');
    const isTouch = window.matchMedia('(hover: none), (pointer: coarse)').matches;

    this.querySelectorAll('.stl-dot[data-tab-id]').forEach((dot) => {
      if (!(dot instanceof HTMLElement)) return;

      if (useHover && !isTouch) {
        dot.addEventListener('mouseenter', () => this.#activate(dot), { signal });
      }

      const trigger = dot.querySelector('.stl-dot__trigger') || dot;
      trigger.addEventListener(
        'click',
        (event) => {
          if (isTouch) {
            event.preventDefault();
            event.stopPropagation();
            this.#toggle(dot);
            return;
          }
          this.#activate(dot);
        },
        { signal }
      );

      trigger.addEventListener(
        'keydown',
        (event) => {
          if (!(event instanceof KeyboardEvent)) return;
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.#toggle(dot);
        },
        { signal }
      );
    });

    // Hover mode: hide product cards when the pointer leaves the look
    if (useHover && !isTouch) {
      this.addEventListener('mouseleave', () => this.#deactivateAll(), { signal });
    }
  }

  /** Close all hotspot cards (and clear selection). Classic side panels stay as-is. */
  #deactivateAll() {
    this.querySelectorAll('.stl-dot[data-tab-id]').forEach((dot) => {
      if (!(dot instanceof HTMLElement)) return;
      dot.removeAttribute('data-opened');
      dot.setAttribute('aria-selected', 'false');
    });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  /**
   * @param {HTMLElement} dot
   */
  #toggle(dot) {
    if (dot.hasAttribute('data-opened')) {
      if (this.#productCardDot) {
        // Keep one active; re-click keeps it open (sync state).
        this.#activate(dot);
        return;
      }
    }
    this.#activate(dot);
  }

  /**
   * @param {HTMLElement} nextDot
   */
  #activate(nextDot) {
    const tabId = nextDot.getAttribute('data-tab-id');
    if (!tabId) return;

    const dots = this.querySelectorAll('.stl-dot[data-tab-id]');
    dots.forEach((dot) => {
      if (!(dot instanceof HTMLElement)) return;
      if (dot === nextDot) {
        dot.setAttribute('data-opened', '');
        dot.setAttribute('aria-selected', 'true');
      } else {
        dot.removeAttribute('data-opened');
        dot.setAttribute('aria-selected', 'false');
      }
    });

    // Classic only: sync side Text panels (Dot title / subtitle / content / CTAs).
    // High energy (product-card-dot): only the hotspot product card — no side panel swap.
    if (this.#productCardDot) return;

    const panels = this.querySelectorAll('.shop-the-look__tab[data-tab-id]');
    panels.forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      if (panel.getAttribute('data-tab-id') === tabId) {
        panel.setAttribute('data-opened', '');
        panel.removeAttribute('hidden');
      } else {
        panel.removeAttribute('data-opened');
        panel.setAttribute('hidden', '');
      }
    });
  }
}

if (!customElements.get('shop-the-look-tabs')) {
  customElements.define('shop-the-look-tabs', ShopTheLookTabs);
}

/** Grid layout hotspot popovers (Look + Hotspot blocks) */
function initShopTheLookGrid(root = document) {
  root.querySelectorAll('[data-stl-hotspot]').forEach((btn) => {
    if (!(btn instanceof HTMLButtonElement)) return;
    if (btn.dataset.stlBound === 'true') return;
    btn.dataset.stlBound = 'true';

    const popover = btn.querySelector('.stl-grid-hotspot__popover');
    const isTouch = window.matchMedia('(hover: none), (pointer: coarse)').matches;

    const close = () => {
      btn.setAttribute('aria-expanded', 'false');
      popover?.setAttribute('hidden', '');
    };

    const open = () => {
      btn.closest('.stl-look')?.querySelectorAll('[data-stl-hotspot]').forEach((other) => {
        if (other !== btn && other instanceof HTMLButtonElement) {
          other.setAttribute('aria-expanded', 'false');
          other.querySelector('.stl-grid-hotspot__popover')?.setAttribute('hidden', '');
        }
      });
      btn.setAttribute('aria-expanded', 'true');
      popover?.removeAttribute('hidden');
    };

    if (!isTouch) {
      btn.addEventListener('mouseenter', open);
      btn.addEventListener('mouseleave', close);
    }

    btn.addEventListener('click', (event) => {
      if (!popover) return;
      event.preventDefault();
      event.stopPropagation();
      if (btn.getAttribute('aria-expanded') === 'true') close();
      else open();
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initShopTheLookGrid());
} else {
  initShopTheLookGrid();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target) initShopTheLookGrid(event.target);
});

export { ShopTheLookTabs, initShopTheLookGrid };
